import { useEffect, useRef, useState } from 'react';
import { ScrollView, StyleSheet, useWindowDimensions, View } from 'react-native';
import { FadeIn } from '../../../components/animations';
import { Box } from '../../../components/primitives/Box';
import { HStack, VStack } from '../../../components/primitives/Stack';
import { Text } from '../../../components/primitives/Text';
import { useTranslation } from '../../../contexts/TranslationContext';
import { useTabNavResetKey } from '../../../hooks/useTabNavResetKey';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';
import type { SettingsSection } from '../types';
import { AccountSection } from './AccountSection';
import { AnalyticsSection } from './AnalyticsSection';
import { AppearanceSection } from './AppearanceSection';
import { BillingSection } from './BillingSection';
import { BoardVisibilitySection } from './BoardVisibilitySection';
import { BusinessContextSection } from './BusinessContextSection';
import { BusinessHoursSection } from './BusinessHoursSection';
import { JobTitleSettingsSection } from './JobTitleSettingsSection';
import { LeadAssignmentSection } from './LeadAssignmentSection';
import { NotificationsSection } from './NotificationsSection';
import { OrganizationSection } from './OrganizationSection';
import { ProfileSection } from './ProfileSection';
import { RegulatoryDocsSection } from './RegulatoryDocsSection';
import { SellerProfilesSection } from './SellerProfilesSection';
import { SettingsNav } from './SettingsNav';
import { TeamSection } from './TeamSection';
import { VersionFooter } from './VersionFooter';
import { VoiceCallingSection } from './VoiceCallingSection';

function ActiveSection({
  section,
  checkoutReturn,
}: Readonly<{ section: SettingsSection; checkoutReturn?: 'return' | 'cancel' }>) {
  switch (section) {
    case 'profile':
      return <ProfileSection />;
    case 'organization':
      return <OrganizationSection />;
    case 'businessContext':
      return <BusinessContextSection />;
    case 'team':
      return <TeamSection />;
    case 'sellerProfiles':
      return <SellerProfilesSection />;
    case 'leadAssignment':
      return <LeadAssignmentSection />;
    case 'jobTitles':
      return <JobTitleSettingsSection />;
    case 'notifications':
      return <NotificationsSection />;
    case 'appearance':
      return <AppearanceSection />;
    case 'analytics':
      return <AnalyticsSection />;
    case 'boardVisibility':
      return <BoardVisibilitySection />;
    case 'businessHours':
      return <BusinessHoursSection />;
    case 'voiceCalling':
      return <VoiceCallingSection />;
    case 'regulatoryDocs':
      return <RegulatoryDocsSection />;
    case 'billing':
      return <BillingSection checkoutReturn={checkoutReturn} />;
    case 'account':
      return <AccountSection />;
  }
}

export function SettingsScreen({
  initialSection,
  checkoutReturn,
}: Readonly<{ initialSection?: SettingsSection; checkoutReturn?: 'return' | 'cancel' }>) {
  const [activeSection, setActiveSection] = useState<SettingsSection>(initialSection ?? 'profile');
  const didMountRef = useRef(false);
  const { width } = useWindowDimensions();
  const isDesktop = width >= 768;
  const { t } = useTranslation();

  // FLOSEAI-433: the Tabs navigator keeps this screen mounted, so the active
  // sub-section survives navigating away and back. Reset to the root section
  // whenever the chrome re-navigates to the Settings tab. resetKey starts at 0,
  // so the initial mount is a no-op (already 'profile').
  const resetKey = useTabNavResetKey('settings');
  useEffect(() => {
    void resetKey;
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    setActiveSection('profile');
  }, [resetKey]);

  useEffect(() => {
    if (initialSection) setActiveSection(initialSection);
  }, [initialSection]);

  return (
    <View testID="settings_screen" style={styles.root}>
      <FadeIn delay={0} translateY={12} style={styles.headerSpacing}>
        <VStack spacing="xs">
          <Text variant="h4" color={colors.text.primary} style={styles.pageTitle}>
            {t('settings.title')}
          </Text>
          <Text variant="caption" color={colors.text.tertiary}>
            {t('settings.subtitle')}
          </Text>
        </VStack>
      </FadeIn>

      {isDesktop ? (
        <HStack spacing="xl" align="stretch" style={styles.desktopRow}>
          <Box style={styles.navColumn}>
            <SettingsNav activeSection={activeSection} onSectionChange={setActiveSection} />
          </Box>
          <Box style={styles.contentColumn}>
            <ScrollView
              style={styles.scroll}
              contentContainerStyle={styles.scrollContent}
              showsVerticalScrollIndicator={false}
            >
              <ActiveSection section={activeSection} checkoutReturn={checkoutReturn} />
              <VersionFooter />
            </ScrollView>
          </Box>
        </HStack>
      ) : (
        <>
          <View style={styles.navSpacing}>
            <SettingsNav activeSection={activeSection} onSectionChange={setActiveSection} />
          </View>
          <ScrollView
            style={styles.scroll}
            contentContainerStyle={styles.scrollContent}
            showsVerticalScrollIndicator={false}
          >
            <ActiveSection section={activeSection} checkoutReturn={checkoutReturn} />
            <VersionFooter />
          </ScrollView>
        </>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    paddingHorizontal: tokens.spacing.xl,
  },
  headerSpacing: {
    marginBottom: tokens.spacing.lg,
  },
  navSpacing: {
    marginBottom: tokens.spacing.lg,
    marginHorizontal: -tokens.spacing.xl,
  },
  desktopRow: {
    flex: 1,
  },
  scroll: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: tokens.spacing['4xl'],
  },
  pageTitle: {
    fontWeight: '700',
  },
  navColumn: {
    width: 240,
    flexShrink: 0,
  },
  contentColumn: {
    flex: 1,
    minWidth: 0,
  },
});
